import { useContext, useCallback, useState, useEffect, useRef } from "react";
import cx from "classnames";
import { FormContext } from "../components/input/FormContext";
import { useDebounceFn } from "./useDebounce";
export var VALIDATION_STATE;
(function (VALIDATION_STATE) {
    VALIDATION_STATE["UNVALIDATED"] = "UNVALIDATED";
    VALIDATION_STATE["PENDING"] = "PENDING";
    VALIDATION_STATE["FAILED"] = "FAILED";
    VALIDATION_STATE["SUCCESS"] = "SUCCESS";
})(VALIDATION_STATE || (VALIDATION_STATE = {}));
export function useValidation(value = undefined, validators = [], forceErrors, showPendingState = false, showSuccessState = false) {
    const context = useContext(FormContext);
    const cachedValue = useRef({});
    const cachedValidators = useRef([]);
    const [validity, setValidity] = useState(VALIDATION_STATE.UNVALIDATED);
    const [errorMessages, setErrorMessages] = useState([]);
    const [hiddenErrors, setHiddenErrors] = useState(true);
    const validateCb = useCallback((newValue) => {
        Promise.all(validators.map((validator) => validator.validate(newValue)))
            .then((responses) => {
            const isValid = responses.every((response) => response.status === VALIDATION_STATE.SUCCESS);
            if (isValid) {
                setValidity(VALIDATION_STATE.SUCCESS);
                setErrorMessages([]);
            }
            else {
                setValidity(VALIDATION_STATE.FAILED);
                setErrorMessages(responses
                    .filter((response) => response.status === VALIDATION_STATE.FAILED)
                    .map((response) => response.message || "Unknown error"));
            }
            if (hiddenErrors) {
                setHiddenErrors(responses.some((response) => response.hideError));
            }
        })
            .catch(() => {
            setValidity(VALIDATION_STATE.FAILED);
            setHiddenErrors(false);
            setErrorMessages(["Unknown server error"]);
        });
    }, [validators, setValidity, hiddenErrors]);
    const debounceValidate = useDebounceFn(validateCb, 500);
    const validate = useCallback((newValue) => {
        setErrorMessages([]);
        if (validators.length > 0) {
            setValidity(VALIDATION_STATE.PENDING);
            debounceValidate(newValue);
        }
        else {
            setValidity(VALIDATION_STATE.SUCCESS);
        }
    }, [debounceValidate, validators]);
    useEffect(() => {
        if (cachedValue.current !== value ||
            !areValidatorsEqual(validators, cachedValidators.current)) {
            validate(value);
        }
        cachedValue.current = value;
        cachedValidators.current = validators;
    }, [validate, value, validators]);
    const showErrors = validators.length > 0 &&
        (forceErrors || (context && context.forceErrors) || !hiddenErrors);
    const validityClasses = cx({
        "lysa-ui-input-validating": showErrors,
    }, { "lysa-ui-input-invalid": validity === VALIDATION_STATE.FAILED }, {
        "lysa-ui-input-valid": validity === VALIDATION_STATE.SUCCESS && showSuccessState,
    }, {
        "lysa-ui-input-pending": validity === VALIDATION_STATE.PENDING && showPendingState,
    }, { "lysa-ui-input-unvalidated": validity === VALIDATION_STATE.UNVALIDATED });
    return [
        validators.length === 0 ? VALIDATION_STATE.SUCCESS : validity,
        showErrors ? errorMessages : [],
        validityClasses,
        () => {
            setHiddenErrors(true);
        },
    ];
}
function areValidatorsEqual(newValidators, oldValidators) {
    if (newValidators.length !== oldValidators.length) {
        return false;
    }
    const newIds = newValidators.map((newValidator) => newValidator.getId());
    const oldIds = oldValidators.map((oldValidator) => oldValidator.getId());
    return newIds.every((id) => oldIds.includes(id));
}
